import { useEffect, useState } from "react";
import { AlarmClock, Loader2, Mail, Plus, Send, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, apiError, fmtWhen } from "@/lib/api";
import { BackupPanel } from "@/components/BackupPanel";
import { useAuth } from "@/context/AuthContext";

const fmtIST = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })} IST`;
};

const Field = ({ label, hint, children }) => (
  <div>
    <div className="label-caps">{label}</div>
    <div className="mt-2">{children}</div>
    {hint && <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">{hint}</p>}
  </div>
);

export default function Settings() {
  const { shows, superAdminEligible } = useAuth();
  const [state, setState] = useState(null);
  const [form, setForm] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState("");
  const [preview, setPreview] = useState(null);
  const [health, setHealth] = useState(null);
  const [savingHealth, setSavingHealth] = useState(false);

  const loadHealth = () =>
    api
      .get("/settings/compliance-health")
      .then(({ data }) => setHealth(data.settings))
      .catch(() => setHealth(null));

  const saveHealth = async () => {
    setSavingHealth(true);
    try {
      const { data } = await api.put("/settings/compliance-health", {
        behind_days: Number(health.behind_days),
        quiet_days: Number(health.quiet_days),
      });
      setHealth(data.settings);
      toast.success(
        data.changed
          ? `Saved — a unit is behind after ${data.settings.behind_days} day(s) and gone quiet after ${data.settings.quiet_days}.`
          : "Nothing changed — the thresholds are already these.",
      );
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSavingHealth(false);
    }
  };

  const load = () =>
    api
      .get("/settings/reminders")
      .then(({ data }) => {
        setState(data);
        setForm({
          ...data.settings,
          digest_excluded: data.settings.digest_excluded || [],
          time: `${String(data.settings.hour).padStart(2, "0")}:${String(
            data.settings.minute,
          ).padStart(2, "0")}`,
        });
      })
      .catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    load();
    loadHealth();
  }, []);

  const addEmail = () => {
    const email = newEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error("That does not look like a valid email address.");
    if ((state?.admin_recipients || []).some((a) => a.email.toLowerCase() === email))
      return toast.error("This officer already receives the digest as an SDTD user.");
    if (form.digest_recipients.includes(email)) return toast.error("That address is already on the list.");
    setForm({ ...form, digest_recipients: [...form.digest_recipients, email] });
    setNewEmail("");
  };

  const excluded = form?.digest_excluded || [];
  const toggleAlways = (email) =>
    setForm({
      ...form,
      digest_excluded: excluded.includes(email)
        ? excluded.filter((x) => x !== email)
        : [...excluded, email],
    });

  const save = async () => {
    const [hour, minute] = form.time.split(":").map(Number);
    setSaving(true);
    try {
      const { data } = await api.put("/settings/reminders", {
        enabled: form.enabled,
        hour,
        minute,
        digest_recipients: form.digest_recipients,
        digest_excluded: form.digest_excluded || [],
        login_reminder_days: Number(form.login_reminder_days),
        update_reminder_days: Number(form.update_reminder_days),
        weekly_rank_cards: form.weekly_rank_cards,
        rank_alerts: form.rank_alerts,
      });
      toast.success(
        data.changed ? "Reminder settings saved." : "Nothing changed — settings already up to date.",
      );
      setState((s) => ({ ...s, settings: data.settings, next_run_at: data.next_run_at }));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const run = async (dryRun) => {
    setRunning(dryRun ? "dry" : "live");
    try {
      const { data } = await api.post("/settings/reminders/run", null, { params: { dry_run: dryRun } });
      setPreview(data);
      toast.success(
        dryRun ? "Preview ready — no email was sent." : `Reminders sent. ${data.summary}`,
      );
      if (!dryRun) load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setRunning("");
    }
  };

  if (!form)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  const admins = state?.admin_recipients || [];

  return (
    <div className="p-5 sm:p-8" data-testid="settings-page">
      <div className="label-caps">Settings</div>
      <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl">
        Automated reminders
      </h1>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        The portal nudges SPOCs on its own and sends SDTD a daily digest. Change the timing, the day
        limits and who receives the digest here.
      </p>

      <div className="mt-8 grid gap-px bg-border lg:grid-cols-3">
        <div className="space-y-7 bg-card p-6 lg:col-span-2">
          <div className="flex items-start justify-between gap-6 border-b border-border pb-6">
            <div>
              <div className="font-display text-base font-bold">Run reminders automatically</div>
              <p className="mt-1 text-xs text-muted-foreground">
                Switch this off to stop all automatic mails. You can still send them by hand below.
              </p>
            </div>
            <Switch
              checked={form.enabled}
              data-testid="reminders-enabled"
              onCheckedChange={(v) => setForm({ ...form, enabled: v })}
            />
          </div>

          <div className="flex items-start justify-between gap-6 border-b border-border pb-6">
            <div>
              <div className="font-display text-base font-bold">Monday leaderboard rank card</div>
              <p className="mt-1 text-xs text-muted-foreground">
                Every unit's SPOC receives their unit's rank card at 9:00 AM IST each Monday, and
                SDTD receives one copy carrying all four boards.
              </p>
            </div>
            <Switch
              checked={form.weekly_rank_cards !== false}
              data-testid="weekly-rank-cards"
              onCheckedChange={(v) => setForm({ ...form, weekly_rank_cards: v })}
            />
          </div>

          <div className="flex items-start justify-between gap-6 border-b border-border pb-6">
            <div>
              <div className="font-display text-base font-bold">Tell a unit when it slips</div>
              <p className="mt-1 text-xs text-muted-foreground">
                When a unit loses a place on its board, its SPOC and designated members are told what
                cost the ground — at most one note a unit a day.
              </p>
            </div>
            <Switch
              checked={form.rank_alerts !== false}
              data-testid="rank-alerts-enabled"
              onCheckedChange={(v) => setForm({ ...form, rank_alerts: v })}
            />
          </div>

          {shows("settings.schedule") && (
          <div className="grid gap-7 sm:grid-cols-3">            <Field label="Run every day at" hint="Indian Standard Time.">
              <Input
                type="time"
                className="h-11 rounded-none font-data"
                data-testid="reminder-time"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
              />
            </Field>
            <Field label="Sign-in reminder after" hint="Days a SPOC may stay away before a nudge.">
              <Input
                type="number"
                min={1}
                max={60}
                className="h-11 rounded-none font-data"
                data-testid="login-reminder-days"
                value={form.login_reminder_days}
                onChange={(e) => setForm({ ...form, login_reminder_days: e.target.value })}
              />
            </Field>
            <Field label="Update reminder after" hint="Days a register may stay untouched.">
              <Input
                type="number"
                min={1}
                max={120}
                className="h-11 rounded-none font-data"
                data-testid="update-reminder-days"
                value={form.update_reminder_days}
                onChange={(e) => setForm({ ...form, update_reminder_days: e.target.value })}
              />
            </Field>
          </div>
          )}

          {shows("settings.digest") && (
          <div className="border-t border-border pt-6">
            <div className="font-display text-base font-bold">Daily digest recipients</div>
            <p className="mt-1 text-xs text-muted-foreground">
              Every SDTD administrator and monitor is on the list to begin with, and any of them can
              be taken off. Add anyone else who should receive it.
            </p>

            <div className="mt-4 flex flex-wrap gap-2" data-testid="always-recipients">
              {admins.map((a) => {
                const off = excluded.includes(a.email.toLowerCase());
                return (
                  <span
                    key={a.email}
                    className={`flex items-center gap-2 border px-3 py-1.5 text-xs ${
                      off ? "border-dashed border-border bg-card" : "border-border bg-muted"
                    }`}
                    data-testid={`always-${a.email}`}
                  >
                    <Mail className="h-3 w-3 text-muted-foreground" />
                    <span className={`font-data ${off ? "line-through opacity-60" : ""}`}>
                      {a.email}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {off ? "taken off" : "SDTD"}
                    </span>
                    <button
                      type="button"
                      data-testid={`toggle-always-${a.email}`}
                      title={off ? "Put them back on the digest" : "Take them off the digest"}
                      className="text-muted-foreground transition-colors duration-200 hover:text-primary"
                      onClick={() => toggleAlways(a.email.toLowerCase())}
                    >
                      {off ? <Plus className="h-3.5 w-3.5" /> : <Trash2 className="h-3.5 w-3.5" />}
                    </button>
                  </span>
                );
              })}
            </div>

            <div className="mt-4 space-y-2" data-testid="extra-recipients">
              {form.digest_recipients.length === 0 && (
                <p className="text-xs text-muted-foreground">No extra addresses added yet.</p>
              )}
              {form.digest_recipients.map((email) => (
                <div
                  key={email}
                  className="flex items-center justify-between border border-border px-3 py-2 text-xs"
                >
                  <span className="font-data">{email}</span>
                  <button
                    type="button"
                    data-testid={`remove-recipient-${email}`}
                    className="text-muted-foreground transition-colors duration-200 hover:text-primary"
                    onClick={() =>
                      setForm({
                        ...form,
                        digest_recipients: form.digest_recipients.filter((x) => x !== email),
                      })
                    }
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-4 flex gap-2">
              <Input
                className="h-11 max-w-sm rounded-none"
                placeholder="officer@sail.in"
                data-testid="new-recipient-email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addEmail()}
              />
              <Button
                variant="outline"
                className="h-11 rounded-none"
                data-testid="add-recipient"
                onClick={addEmail}
              >
                <Plus className="mr-2 h-4 w-4" /> Add
              </Button>
            </div>
          </div>
          )}

          <div className="flex flex-wrap gap-3 border-t border-border pt-6">
            <Button
              className="h-11 rounded-none font-display text-xs font-bold uppercase tracking-widest"
              disabled={saving}
              data-testid="save-settings"
              onClick={save}
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save settings
            </Button>
            <Button
              variant="outline"
              className="h-11 rounded-none"
              disabled={!!running}
              data-testid="preview-reminders"
              onClick={() => run(true)}
            >
              {running === "dry" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              Preview (no mail sent)
            </Button>
            <Button
              variant="outline"
              className="h-11 rounded-none"
              disabled={!!running}
              data-testid="run-reminders"
              onClick={() => run(false)}
            >
              {running === "live" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              Send reminders now
            </Button>
          </div>
        </div>

        <div className="space-y-6 bg-card p-6">
          <div>
            <div className="label-caps">Next automatic run</div>
            <div className="font-data mt-2 flex items-center gap-2 text-lg font-bold">
              <AlarmClock className="h-4 w-4 text-primary" />
              <span data-testid="next-run-at">
                {state?.next_run_at ? fmtIST(state.next_run_at) : "Switched off"}
              </span>
            </div>
          </div>

          <div className="border-t border-border pt-6">
            <div className="label-caps">Last run</div>
            {state?.last_run?.last_run_at ? (
              <div className="mt-2 space-y-2 text-xs">
                <div className="font-data">{fmtWhen(state.last_run.last_run_at)}</div>
                <p className="leading-relaxed text-muted-foreground">{state.last_run.summary}</p>
                <div className="grid grid-cols-2 gap-px bg-border">
                  <div className="bg-card p-2">
                    <div className="text-[10px] uppercase text-muted-foreground">Sign-in nudges</div>
                    <div className="font-data text-base font-bold">
                      {state.last_run.login_reminders?.length || 0}
                    </div>
                  </div>
                  <div className="bg-card p-2">
                    <div className="text-[10px] uppercase text-muted-foreground">Update nudges</div>
                    <div className="font-data text-base font-bold">
                      {state.last_run.update_reminders?.length || 0}
                    </div>
                  </div>
                </div>
                {state.last_run.updated_by && (
                  <p className="text-[11px] text-muted-foreground">
                    Settings last changed by {state.last_run.updated_by}
                  </p>
                )}
              </div>
            ) : (
              <p className="mt-2 text-xs text-muted-foreground">Not run yet.</p>
            )}
          </div>

          {state?.settings?.updated_by && (
            <div className="border-t border-border pt-6">
              <div className="label-caps">Last changed</div>
              <p className="mt-2 text-xs text-muted-foreground">
                {state.settings.updated_by}
                <br />
                {fmtWhen(state.settings.updated_at)}
              </p>
            </div>
          )}
        </div>
      </div>

      {health && (
        <div
          className="mt-8 border border-border bg-card p-6"
          data-testid="compliance-health-settings"
        >
          <div className="label-caps">Compliance data health</div>
          <h2 className="mt-1 font-display text-lg font-bold">
            When a unit counts as behind, and when as gone quiet
          </h2>
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
            These two numbers are what the Dashboard's compliance strip reads. They are counted in
            days since a usecase's last reading — a unit that filled yesterday is always up to
            date.
          </p>
          <div className="mt-5 grid max-w-2xl gap-6 sm:grid-cols-2">
            <Field label="Behind after" hint="Days with no reading before a unit is called behind.">
              <Input
                type="number"
                min="1"
                max="30"
                className="h-11 w-28 rounded-none"
                data-testid="compliance-behind-days"
                value={health.behind_days}
                onChange={(e) => setHealth({ ...health, behind_days: e.target.value })}
              />
            </Field>
            <Field
              label="Gone quiet after"
              hint="Days with no reading before the unit is called gone quiet. It must be the larger of the two."
            >
              <Input
                type="number"
                min="2"
                max="180"
                className="h-11 w-28 rounded-none"
                data-testid="compliance-quiet-days"
                value={health.quiet_days}
                onChange={(e) => setHealth({ ...health, quiet_days: e.target.value })}
              />
            </Field>
          </div>
          <Button
            className="mt-5 h-11 rounded-none font-display text-xs font-bold uppercase tracking-widest"
            disabled={savingHealth}
            data-testid="save-compliance-health"
            onClick={saveHealth}
          >
            {savingHealth ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save these thresholds
          </Button>
          {health.updated_by && (
            <p className="mt-3 text-[11px] text-muted-foreground">
              Last changed by {health.updated_by} · {fmtWhen(health.updated_at)}
            </p>
          )}
        </div>
      )}

      {superAdminEligible && <BackupPanel />}

      {preview && (
        <div className="mt-8 border border-border bg-card p-6" data-testid="reminder-preview">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-bold">
              {preview.dry_run ? "Preview — nothing was sent" : "Reminder run finished"}
            </h2>
            <button
              type="button"
              className="text-xs underline"
              data-testid="close-preview"
              onClick={() => setPreview(null)}
            >
              Close
            </button>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">{preview.summary}</p>
          <div className="mt-4 grid gap-6 md:grid-cols-2">
            <div>
              <div className="label-caps mb-2">Digest goes to ({preview.digest_recipients.length})</div>
              <ul className="space-y-1 text-xs">
                {preview.digest_recipients.map((e) => (
                  <li key={e} className="font-data">
                    {e}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="label-caps mb-2">What the digest says</div>
              <ul className="space-y-1 text-xs leading-relaxed">
                {preview.lines.length === 0 && <li>Everything is up to date.</li>}
                {preview.lines.map((line, i) => (
                  <li key={i} dangerouslySetInnerHTML={{ __html: line }} />
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
